import React, { useEffect, useRef, useState } from "react";
import { track } from "@vercel/analytics";
import mixpanel from "../../mixpanel";
import "./KidsPage.css";
import tatevikImg from "../../assets/tatevik_illustration.png";
import aniImg from "../../assets/ani_illustration.png";
import idramLogo from "../../assets/idram_icon.png";
import plannerBookImg from "../../assets/planner_book.png";

interface CardProps {
  children: React.ReactNode;
  className?: string;
  animated?: boolean;
}

const SnapCard = React.forwardRef<HTMLElement, CardProps>(
  ({ children, className = "", animated = false }, sectionRef) => (
    <section className="kids-section" ref={sectionRef}>
      <div className={`kids-card ${className} ${animated ? "kids-animate" : ""}`}>
        {children}
      </div>
    </section>
  )
);

// Card indices: 0=Hero, 1=VereV, 2=CapyNova, 3=Planner, 4=Idram, 5=RichDad
const CAPY_IDX    = 2;
const PLANNER_IDX = 3;

export default function KidsPage() {
  const [aniCard, setAniCard]   = useState<"capy" | "planner" | null>(null);
  const [animated, setAnimated] = useState<Set<number>>(new Set([0]));
  const containerRef            = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Prevent body-level scroll (iOS Safari 100vh overflow)
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const onScroll = () => {
      const h = el.clientHeight;
      if (!h) return;
      const idx = Math.round(el.scrollTop / h);

      // Animate all slides up to and including the current one
      setAnimated(prev => {
        if (prev.has(idx)) return prev;
        const next = new Set(prev);
        next.add(idx);
        return next;
      });

      // Ani fixed element: visible on CapyNova and Planner slides
      if (idx === PLANNER_IDX)     setAniCard("planner");
      else if (idx === CAPY_IDX)   setAniCard("capy");
      else                         setAniCard(null);
    };

    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll(); // initialise on mount
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      {/* ── Fixed Ani ── */}
      <div className={`ani-fixed ${aniCard ? "ani-visible" : ""} ${aniCard === "planner" ? "ani-is-planner" : ""}`}>
        <div className="ani-fixed-text">
          <div className="ani-bubble ani-bubble-capy">
            <div className="capy-founder-badge">✦ Հիմնադիր</div>
            <div className="capy-speech">
              Ողջույն, ես Անին եմ, Capynova-ի հիմնադիրը։ Հարթակում երեխաները սովորում են այն, ինչ դպրոցում հաճախ չեն սովորեցնում։
            </div>
          </div>
          <div className="ani-bubble ani-bubble-planner">
            <div className="planner-founder-badge">✦ Հեղինակ · 19 տ.</div>
            <div className="planner-speech">
              Ես 19 տարեկանում ստեղծել եմ Extra Planner-ը, քանի որ երեխաներին ուղորդող ոչ մի գրքույկ չկա։
            </div>
          </div>
        </div>
        <img src={aniImg} alt="Անի" className="ani-fixed-img" />
      </div>

      <div className="kids-container" ref={containerRef}>

        {/* ── Card 1: Hero ── */}
        <SnapCard className="kids-hero" animated={animated.has(0)}>
          {/* Sparkles */}
          <div className="hero-sparkles" aria-hidden="true">
            {Array.from({ length: 22 }).map((_, i) => (
              <span key={i} className={`hstar hstar-${["gold","white","pink"][i % 3]} hstar-${i % 6}`} style={{
                left: `${(i * 43 + 11) % 100}%`, top: `${(i * 59 + 7) % 88}%`,
                animationDelay: `${(i * 0.35) % 3.5}s`,
                width: `${2 + (i % 3)}px`, height: `${2 + (i % 3)}px`,
              }} />
            ))}
            {/* 4-point sparkle crosses */}
            {[
              { left: "18%", top: "22%", size: 14, delay: "0s" },
              { left: "75%", top: "15%", size: 10, delay: "1.2s" },
              { left: "88%", top: "55%", size: 16, delay: "0.5s" },
              { left: "10%", top: "68%", size: 12, delay: "2s" },
              { left: "55%", top: "80%", size: 10, delay: "0.8s" },
              { left: "42%", top: "12%", size: 8,  delay: "1.7s" },
            ].map((s, i) => (
              <span key={`x${i}`} className="hstar-cross" style={{
                left: s.left, top: s.top,
                width: `${s.size}px`, height: `${s.size}px`,
                animationDelay: s.delay,
              }} />
            ))}
          </div>
          <div className="kids-hero-inner">
            <span className="kids-tag">📚 EdTech & Գրքեր</span>
            <h1 className="kids-hero-title">
              EdTech-ն ու գրքերը<br />
              <span className="kids-highlight">ապագայի հենարանն</span><br />
              են
            </h1>
            <p className="kids-hero-sub">Ահա մի քանի ռեսուրսներ</p>
          </div>
          <div className="kids-hero-scroll" aria-hidden="true">
            <span className="kids-scroll-label">Scroll</span>
            <div className="kids-scroll-arrow">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M10 4v12M4 10l6 6 6-6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </SnapCard>

        {/* ── Card 2: VereV ── */}
        <SnapCard className="kids-verev" animated={animated.has(1)}>
          <div className="vv-stars" aria-hidden="true">
            {Array.from({ length: 24 }).map((_, i) => (
              <span key={i} className={`vstar vstar-${i % 6}`} style={{
                left: `${(i * 37 + 11) % 100}%`, top: `${(i * 53 + 7) % 85}%`,
                animationDelay: `${(i * 0.3) % 3}s`,
                width: `${2 + (i % 3)}px`, height: `${2 + (i % 3)}px`,
              }} />
            ))}
          </div>
          <div className="vv-glow-a" /><div className="vv-glow-b" />
          <div className="vv-main">
            <span className="vv-badge">🎮 Ֆինանսական Խաղ</span>
            <h2 className="vv-title">VereV</h2>
            <div className="vv-pills">
              <span>👨‍👩‍👧 Ընտանիք</span>
              <span>🃏 Քարտեր</span>
              <span>💡 Ֆինանսներ</span>
            </div>
            <p className="vv-desc">
              Verev-ը օնլայն քարտային խաղ է, որտեղ երեխան հարցեր է տալիս
              և ստանում պատասխաններ ծնողից։ Երեխան սովորում է խնայելու,
              ունակություններ ձեռքբերելու, աշխատելու կարևորությունը,
              ինչպես նաև հասկանում է, որ գնաճն իրական է, կյանքն էլ՝
              անկանխատեսելի։
            </p>
            <a href="/vereV" className="vv-cta" onClick={() => { track("kids_verev_cta_click"); mixpanel.track("kids_verev_cta_clicked"); }}>Խաղալ հիմա ✦</a>
          </div>
          <div className="vv-founder">
            <div className="vv-founder-text">
              <div className="vv-founder-badge">✦ Հիմնադիր</div>
              <div className="vv-speech">
                Ողջույն, ես Տաթևիկն եմ, girlsandfinances բլոգի հիմնադիրը։
                Ես կարծում եմ, որ Verev-ն ամենահաճելին է, երբ երեխան սկսում
                է հետաքրքրվել քարտերով։
              </div>
            </div>
            <img src={tatevikImg} alt="Տաթևիկ" className="vv-founder-img" />
          </div>
        </SnapCard>

        {/* ── Card 3: CapyNova ── */}
        <SnapCard className="kids-capy" animated={animated.has(2)}>
          <div className="capy-stars" aria-hidden="true">
            {Array.from({ length: 28 }).map((_, i) => (
              <span key={i} className={`cpstar cpstar-${i % 6}`} style={{
                left: `${(i * 41 + 7) % 100}%`, top: `${(i * 59 + 13) % 88}%`,
                animationDelay: `${(i * 0.27) % 3.2}s`,
                width: `${2 + (i % 4)}px`, height: `${2 + (i % 4)}px`,
              }} />
            ))}
          </div>
          <div className="capy-glow-a" /><div className="capy-glow-b" /><div className="capy-glow-c" />
          <div className="capy-main">
            <span className="vv-badge capy-badge">🦫 Հավելված</span>
            <h2 className="capy-title">CapyNova</h2>
            <p className="capy-desc">
              Ֆինանսական գրագիտությունից և ձեռներեցությունից մինչև արհեստական բանականություն, առողջություն, ինքնակազմակերպում, քննադատական մտածողություն և որոշումների կայացում՝ Capynova-ի նպատակն է երեխաներին տալ այն հմտությունները, որոնք անհրաժեշտ են ոչ միայն դպրոցում հաջողելու, այլև սեփական ապագան կառուցելու համար։
            </p>
            <a href="https://capynova.com" target="_blank" rel="noopener noreferrer" className="vv-cta capy-cta" onClick={() => { track("kids_capynova_cta_click"); mixpanel.track("kids_capynova_cta_clicked"); }}>
              Բացել capynova.com ✦
            </a>
          </div>
        </SnapCard>

        {/* ── Card 4: Extra Smart Planner ── */}
        <SnapCard className="kids-planner" animated={animated.has(3)}>
          <div className="planner-stars" aria-hidden="true">
            {Array.from({ length: 28 }).map((_, i) => (
              <span key={i} className={`pstar pstar-${i % 6}`} style={{
                left: `${(i * 43 + 17) % 100}%`, top: `${(i * 61 + 9) % 88}%`,
                animationDelay: `${(i * 0.31) % 3.4}s`,
                width: `${2 + (i % 4)}px`, height: `${2 + (i % 4)}px`,
              }} />
            ))}
          </div>
          <div className="planner-glow-a" /><div className="planner-glow-b" /><div className="planner-glow-c" />
          <img src={plannerBookImg} alt="Extra Smart Planner" className="planner-book-img" />
          <div className="planner-main">
            <span className="vv-badge planner-badge">📓 Գիրք / Պլաններ</span>
            <h2 className="planner-title">Extra Smart<br />Planner</h2>
            <p className="planner-desc">
              Այս գրքույկն օգնում է նշումներ անել, ինֆորմացվել, զվարճանալ և, ինչու ոչ, իմանալ, թե ինչպես կարող ես գումար վաստակել Խելացի Պլանեռի օգնությամբ։
              <br /><br />
              Պլանեռն օգնում է բացահայտել քեզ, կառավարել միջոցները, ինքնակրթվել և հոգ տանել։
            </p>
            <a href="https://www.instagram.com/extraplanner/" target="_blank" rel="noopener noreferrer" className="vv-cta planner-cta" onClick={() => { track("kids_planner_cta_click"); mixpanel.track("kids_planner_cta_clicked"); }}>
              Տեսնել Instagram-ում ✦
            </a>
          </div>
        </SnapCard>

        {/* ── Card 5: Idram Junior ── */}
        <SnapCard className="kids-idram" animated={animated.has(4)}>
          <div className="idram-glow-a" />
          <div className="idram-glow-b" />
          <div className="idram-glow-c" />

          <div className="idram-inner">
            <img src={idramLogo} alt="Idram" className="idram-logo" />
            <div className="idram-divider" />
            <h2 className="idram-title">Idram Junior</h2>
            <p className="idram-desc">
              Idram-ի հավելված երեխաների համար — սովորեք կառավարել գումարը, ստացեք գրպանի փող և զարգացրեք ֆինանսական սովորություններ։
            </p>
            <a href="https://www.idram.am/idram-junior" target="_blank" rel="noopener noreferrer" className="idram-cta" onClick={() => { track("kids_idram_cta_click"); mixpanel.track("kids_idram_cta_clicked"); }}>
              Բացել Idram Junior →
            </a>
          </div>
        </SnapCard>


      </div>
    </>
  );
}
